import React from "react";
import {
  GraduationCap,
  Handshake,
  HeartPulse
} from "lucide-react";

// reactstrap components
import {
  Container
} from "reactstrap";

import {
  Switch,
  Route,
  useRouteMatch
} from "react-router-dom";

// core components
import MainNavbar from "components/Navbars/MainNavbar.js";
import LandingPageHeader from "components/Headers/LandingPageHeader.js";
import DefaultFooter from "components/Footers/DefaultFooter.js";
import SEO from "components/SEO/SEO.js";
import {
  LitterListingGrid,
  useLandingPageClasses
} from "components/Litters/LitterComponents.js";
import VH2022Page from "./VH2022Page.js";
import VR2027Page from "./VR2027Page.js";
import SF2027Page from "./SF2027Page.js";
import VR2023Page from "./VR2023Page.js";
import NJ2025Page from "./NJ2025Page.js";

const upcomingLitters = [
  {
    title: "Venus × Rival (2026)",
    href: "/litters/venus-rival-2027",
    image: require("../../assets/img/dogs/litters/vr2027/announcement.jpg"),
    status: "Border Collie Litter",
  },
  {
    title: "Squeeze × Fahren (2027)",
    href: "/litters/squeeze-fahren-2027",
    image: require("../../assets/img/dogs/litters/sf2027/announcement.jpg"),
    status: "Sport Mix Litter",
  }
];

const previousLitters = [
  {
    title: "Nimona × Jerome (2025)",
    href: "/litters/nimona-jerome-2025",
    image: require("../../assets/img/dogs/litters/nj2025/announcement4.jpg"),
    status: "Sport Mix Litter",
  },
  {
    title: "Venus × Rip (2023)",
    href: "/litters/venus-rip-2023",
    image: require("../../assets/img/dogs/litters/vr2023/announcement.jpg"),
    status: "Border Collie Litter",
  },
  {
    title: "Venus × HopSing (2022)",
    href: "/litters/venus-hopsing-2022",
    image: require("../../assets/img/dogs/litters/vh2022/announcement.jpg"),
    status: "Sport Mix Litter",
  }
];

const puppyProgramNotes = [
  {
    icon: GraduationCap,
    title: "Early Foundations",
    text: "Puppies are raised with information from Puppy Culture and Avidog programs. They also get Early Scent Introduction and a light Early Neurological Stimulation protocol. Daily updates are posted in a Facebook group."
  },
  {
    icon: HeartPulse,
    title: "Health Screening",
    text: "Puppies have BAER hearing and CAER eye exams before going home, and their first shots at 7 weeks. They come with an extensive genetic health and orthopedic guarantee. Parents health testing is available on the litter pages."
  },
  {
    icon: Handshake,
    title: "Thoughtful Placement",
    text: "Homes are chosen by best match, not first to inquire. Puppy selections are made collaboratively with the future owner and are based on the owner's goals and what each puppy is naturally suited for, not on looks alone."
  }
];

function PageSectionHeading({ eyebrow, title, children }) {
  return (
    <div className="index-section-heading">
      {eyebrow && <p>{eyebrow}</p>}
      <h2>{title}</h2>
      <span aria-hidden="true"></span>
      {children}
    </div>
  );
}

function LittersPage() {
  let { path } = useRouteMatch();
  useLandingPageClasses();

  return (
      <>
      <Switch>
        <Route exact path={path}>
          <SEO
            title="Litters | Border Collie & Sport Mix Puppies"
            description="View current, upcoming, and previous Endgame Sport Dogs litters near Raleigh NC, including border collie puppies and sport mix puppies bred for agility, flyball, dock diving, herding, and disc."
            path="/litters"
          />
          <MainNavbar />
          <div className="wrapper litters-index-page site-bg-light-grey">
            <LandingPageHeader
              bg="header4"
              hideText
              title="Border Collie & Sport Mix Litters"
              subtitle="Current, planned, and previous sport-bred puppies from Endgame Sport Dogs."
            />
            <main className="index-page-main site-bg-light-grey">
              <section className="index-intro-section litters-info-section">
                <Container>
                  <PageSectionHeading title="Information on our litters">
                  </PageSectionHeading>
                  <p>
                      Litters are planned based on current competition schedules and are subject to change. Puppy prices are usually $2000-2500 but may range based on costs associated with a particular breeding and number of puppies in the litter. I maintain an interested list for each litter but do not take deposits or guarantee spots.  All puppies are sold as pets (non-breeding) initially but I will allow breeding if the dog demonstrates breeding worthy qualities, is fully health tested, and breedings are approved by me. Co-ownerships are occasionally available and offer a free or reduced price puppy depending on the contract decided between myself and the co-owner. Junior handlers are welcome to reach out if they are interested in a dog and may need assistance.
                  </p>
                  <div className="program-note-grid">
                    {puppyProgramNotes.map(note => {
                      const Icon = note.icon;

                      return (
                        <article className="program-note" key={note.title}>
                          <Icon aria-hidden="true" size={36} strokeWidth={2} />
                          <div>
                            <h3>{note.title}</h3>
                            <p>{note.text}</p>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </Container>
              </section>

              <section className="listing-section litters-upcoming-section site-bg-dark-navy">
                <Container>
                  <PageSectionHeading title="Upcoming Litters">
                    <p>
                      Venus will be bred in late 2026 and mid 2027. She will have a border collie litter followed by a border jack litter.
                    </p>
                    <p>
                      Squeeze will be bred in 2027 to a papillon. She will have a border collie litter in the future.
                    </p>
                  </PageSectionHeading>
                  <LitterListingGrid
                    litters={upcomingLitters}
                    rowClassName="litter-feature-row"
                    cardClassName="litter-nameplate-card litter-feature-card"
                  />
                </Container>
              </section>

              <section className="listing-section listing-section-alt ">
                <Container>
                  <PageSectionHeading title="Previous Litters" />
                  <LitterListingGrid
                    litters={previousLitters}
                    rowClassName="litter-gallery-grid"
                    cardClassName="litter-nameplate-card litter-gallery-card"
                  />
                </Container>
              </section>
            </main>
            <DefaultFooter />
          </div>
        </Route>
        <Route path={`${path}/venus-hopsing-2022`}>
          <VH2022Page />
        </Route>
        <Route path={`${path}/venus-rip-2023`}>
          <VR2023Page />
        </Route>
        <Route path={`${path}/nimona-jerome-2025`}>
          <NJ2025Page />
        </Route>
        <Route path={`${path}/venus-rival-2027`}>
          <VR2027Page />
        </Route>
        <Route path={`${path}/squeeze-fahren-2027`}>
          <SF2027Page />
        </Route>
      </Switch>
    </>
  );
}

export default LittersPage;
